import type {NumberField} from '@prismicio/client'
import type {CarouselSliceDefaultPrimaryCardsItem} from 'prismicio-types'
import type {Dispatch, SetStateAction} from 'react'
import {useEffect, useRef} from 'react'
import 'swiper/css'
import 'swiper/css/pagination'
import {Autoplay, Pagination} from 'swiper/modules'
import type {SwiperClass} from 'swiper/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Slide} from './Slide'

export const Slider = ({
  setActiveIndex,
  setSwiperInstance,
  updateProgress,
  cards,
  sliceType,
  componentIndex,
  pageUid,
  duration,
}: {
  setActiveIndex: Dispatch<SetStateAction<number>>
  setSwiperInstance: Dispatch<SetStateAction<SwiperClass | null>>
  updateProgress: (progress: number) => void
  cards: CarouselSliceDefaultPrimaryCardsItem[]
  sliceType?: string
  componentIndex?: number
  pageUid?: string
  duration: NumberField
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  //Get the maximum height of the slides and set it to all slides
  useEffect(() => {
    const updateHeights = () => {
      if (!containerRef.current) {
        return
      }

      const slides = containerRef.current.querySelectorAll('.swiper-slide > div')
      let maxHeight = 0

      slides.forEach((slide) => {
        ;(slide as HTMLElement).style.height = 'auto'
        const height = (slide as HTMLElement).offsetHeight

        if (height > maxHeight) {
          maxHeight = height
        }
      })

      slides.forEach((slide) => {
        ;(slide as HTMLElement).style.height = `${maxHeight}px`
      })
    }

    updateHeights()

    window.addEventListener('resize', updateHeights)
    return () => window.removeEventListener('resize', updateHeights)
  }, [cards])

  return (
    <div ref={containerRef}>
      <Swiper
        autoplay={{
          delay: (duration ?? 8) * 1000,
        }}
        className="!overflow-visible lg:w-full lg:max-w-full"
        modules={[Pagination, Autoplay]}
        onActiveIndexChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        spaceBetween={0}
        slidesPerView="auto"
        speed={800}
        onSwiper={(swiper) => setSwiperInstance(swiper)}
        wrapperClass="custom-ease min-h-full"
        onAutoplayTimeLeft={(_, __, progress) => {
          updateProgress(progress)
        }}
      >
        {cards.map((card, index) => (
          <SwiperSlide className="w-full rounded-lg bg-green-dark lg:max-w-[1176px]" key={index}>
            {({isActive}) => (
              <Slide
                card={card}
                key={index}
                index={index}
                isActive={isActive}
                sliceType={sliceType}
                componentIndex={componentIndex}
                pageUid={pageUid}
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}
